import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "./PageHeader.tsx";
import { TAB_DESCRIPTIONS } from "../constants/tabDescriptions.ts";
import "./Leaderboard.css";

interface LeaderboardPlayer {
  rank: number;
  username: string;
  display_name: string | null;
  avatar_url?: string;
  current_rating: number;
  peak_rating: number;
  total_games: number;
  wins: number;
  losses: number;
  draws: number;
  win_rate: number;
  favorite_variant: string;
  last_game_at: string;
}

type SortColumn = 'rank' | 'player' | 'rating' | 'games' | 'wins' | 'winRate';
type SortDirection = 'asc' | 'desc';

// ✅ FIXED: Use environment variable and strip /api suffix
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');

// Rating system description for the info card
const RATING_INFO = {
  title: "ELO Rating System",
  description: "Player ratings use the standard ELO rating system, the same method used by FIDE for international chess rankings.",
  details: [
    { label: "Starting Rating", value: "1200" },
    { label: "K-Factor (K)", value: "32" },
    { label: "Rating Floor", value: "0" },
  ],
  formulas: {
    expected: "E = 1 / (1 + 10^((Opponent - Your Rating) / 400))",
    newRating: "New Rating = Old Rating + K × (Score - Expected)",
    scoreWin: "Score = 1 (win), 0.5 (draw), 0 (loss)"
  },
  blendedInfo: "Your Blended ELO is a weighted average of all your variant ratings, where each variant's rating is weighted by the number of games you've played in that variant. This means variants you play more frequently have a greater influence on your overall rating.",
  blendedFormula: "Blended ELO = Σ(Variant Rating × Games Played) / Total Games",
  variantInfo: "Each official variant (Traditional FIDE, Perfect Chess, Breakout Chess, Next Level Chess, Arrival Chess) tracks its own separate ELO rating. This lets you see your skill level for each specific board size and ruleset.",
  customVariantNote: "Custom variants created in the Variant Creator are NOT included in ELO ratings. Only official preset variants affect your rating to ensure fair and consistent competition."
};

const Leaderboard: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState("all");
  const [sortColumn, setSortColumn] = useState<SortColumn>('rank');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showRatingInfo, setShowRatingInfo] = useState(false);
  const [showEloModal, setShowEloModal] = useState(false);
  const navigate = useNavigate();

  // Determine if showing overall/blended rating or variant-specific
  const isOverallRating = selectedVariant === "all";
  const ratingLabel = isOverallRating ? "Overall Rating" : `${selectedVariant} Rating`;

  const variants = [
    "all",
    "Traditional FIDE",
    "Perfect Chess",
    "Breakout Chess",
    "Next Level Chess",
    "Arrival Chess",
  ];

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedVariant]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      // ✅ FIXED: Use API_BASE_URL instead of hardcoded localhost
      const response = await fetch(
        `${API_BASE_URL}/api/leaderboard?variant=${selectedVariant}&limit=100`
      );
      
      // ✅ ADDED: Error checking
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      if (data.success) {
        setLeaderboard(data.leaderboard);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with ascending as default
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedLeaderboard = (): LeaderboardPlayer[] => {
    const sorted = [...leaderboard].sort((a, b) => {
      let compareA: any;
      let compareB: any;

      switch (sortColumn) {
        case 'rank':
          compareA = a.rank;
          compareB = b.rank;
          break;
        case 'player':
          compareA = a.username.toLowerCase();
          compareB = b.username.toLowerCase();
          break;
        case 'rating':
          compareA = a.current_rating;
          compareB = b.current_rating;
          break;
        case 'games':
          compareA = a.total_games;
          compareB = b.total_games;
          break;
        case 'wins':
          compareA = a.wins;
          compareB = b.wins;
          break;
        case 'winRate':
          compareA = a.win_rate;
          compareB = b.win_rate;
          break;
        default:
          return 0;
      }

      if (compareA < compareB) return sortDirection === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) {
      return <span className="sort-icon inactive">↕</span>;
    }
    return (
      <span className="sort-icon active">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getRankColor = (rank: number): string => {
    if (rank === 1) return "#FFD700"; // Gold
    if (rank === 2) return "#C0C0C0"; // Silver
    if (rank === 3) return "#CD7F32"; // Bronze
    return "#4a5568";
  };

  const getRankIcon = (rank: number): string => {
    if (rank === 1) return "👑";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return "";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) {
    return <div className="leaderboard-loading">Loading leaderboard...</div>;
  }

  const sortedLeaderboard = getSortedLeaderboard();

  return (
    <div className="leaderboard-container">
      <PageHeader
        icon={TAB_DESCRIPTIONS.leaderboard.icon}
        title={TAB_DESCRIPTIONS.leaderboard.label}
        description={TAB_DESCRIPTIONS.leaderboard.description}
        actions={
          <>
            <label style={{ color: '#4a5568', fontWeight: 700, fontSize: '16px' }}>
              Filter by Variant:
            </label>
            <select
              className="action-select"
              value={selectedVariant}
              onChange={(e) => setSelectedVariant(e.target.value)}
              style={{ borderColor: '#10b981', borderWidth: '2px' }}
            >
              {variants.map((variant) => (
                <option key={variant} value={variant}>
                  {variant === "all" ? "All Variants" : variant}
                </option>
              ))}
            </select>
          </>
        }
      />

      {leaderboard.length === 0 ? (
        <div className="leaderboard-empty">
          <h3>No Players Found</h3>
          <p>
            {selectedVariant === "all"
              ? "The leaderboard is empty. Run the seeding script to populate it with test data."
              : `No players found for variant: ${selectedVariant}`}
          </p>
          {selectedVariant !== "all" && (
            <button
              onClick={() => setSelectedVariant("all")}
              className="reset-filter-button"
            >
              Show All Players
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Rating Info Card - Item 3: Description of Rating */}
          <div className="rating-info-card">
            <div className="rating-info-header" onClick={() => setShowRatingInfo(!showRatingInfo)}>
              <div className="rating-info-title">
                <span className="rating-info-icon">📊</span>
                <span>{RATING_INFO.title}</span>
              </div>
              <span className="rating-info-toggle">{showRatingInfo ? '▲' : '▼'}</span>
            </div>
            {showRatingInfo && (
              <div className="rating-info-content">
                <p className="rating-info-description">{RATING_INFO.description}</p>
                <div className="rating-info-details">
                  {RATING_INFO.details.map((detail, idx) => (
                    <div key={idx} className="rating-detail-item">
                      <span className="rating-detail-label">{detail.label}:</span>
                      <span className="rating-detail-value">{detail.value}</span>
                    </div>
                  ))}
                </div>
                <div className="rating-info-types">
                  <div className="rating-type-item blended">
                    <strong>Blended Rating (All Variants):</strong>
                    <p>{RATING_INFO.blendedInfo}</p>
                  </div>
                  <div className="rating-type-item variant">
                    <strong>Variant-Specific Rating:</strong>
                    <p>{RATING_INFO.variantInfo}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="leaderboard-stats">
            <div className="stat-card">
              <div className="stat-value">{leaderboard.length}</div>
              <div className="stat-label">Total Players</div>
            </div>
            <div className="stat-card highlight">
              <div className="stat-value">
                {leaderboard[0]?.current_rating || 0}
              </div>
              <div className="stat-label">
                {isOverallRating ? "Top Overall Rating" : `Top ${selectedVariant} Rating`}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {Math.round(
                  leaderboard.reduce((sum, p) => sum + p.total_games, 0) /
                    (leaderboard.length || 1)
                )}
              </div>
              <div className="stat-label">
                {isOverallRating ? "Avg Games (All)" : `Avg ${selectedVariant} Games`}
              </div>
            </div>
            <div
              className="stat-card info clickable"
              onClick={() => setShowEloModal(true)}
              title="Click to learn about ELO ratings"
            >
              <div className="stat-value" style={{ fontSize: '1.2rem' }}>
                {isOverallRating ? "🌐" : "🎯"}
              </div>
              <div className="stat-label">
                {isOverallRating ? "Blended ELO" : "Variant ELO"}
              </div>
              <div className="click-hint">Click for info</div>
            </div>
          </div>

          <div className="leaderboard-table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th className="sortable-header" onClick={() => handleSort('rank')}>
                    Rank <SortIcon column="rank" />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('player')}>
                    Player <SortIcon column="player" />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('rating')}>
                    {isOverallRating ? "Overall Rating" : "Variant Rating"} <SortIcon column="rating" />
                  </th>
                  <th>Peak</th>
                  <th className="sortable-header" onClick={() => handleSort('games')}>
                    Games <SortIcon column="games" />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('wins')}>
                    Wins <SortIcon column="wins" />
                  </th>
                  <th>Losses</th>
                  <th>Draws</th>
                  <th className="sortable-header" onClick={() => handleSort('winRate')}>
                    Win % <SortIcon column="winRate" />
                  </th>
                  <th>Favorite Variant</th>
                  <th>Last Game</th>
                </tr>
              </thead>
              <tbody>
                {sortedLeaderboard.map((player) => (
                  <tr key={player.username} className="player-row">
                    <td className="rank-cell">
                      <span
                        className="rank-number"
                        style={{ color: getRankColor(player.rank) }}
                      >
                        {getRankIcon(player.rank)} {player.rank}
                      </span>
                    </td>
                    <td className="player-cell">
                      <div className="player-info">
                        <div className="player-avatar">
                          {player.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="player-names">
                          <div className="player-username">
                            {player.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="rating-cell">
                      <strong>{player.current_rating}</strong>
                    </td>
                    <td className="peak-cell">{player.peak_rating}</td>
                    <td className="games-cell">{player.total_games}</td>
                    <td className="win-cell">
                      <span className="win">{player.wins}</span>
                    </td>
                    <td className="lose-cell">
                      <span className="loss">{player.losses}</span>
                    </td>
                    <td className="draw-cell">
                      <span className="draw">{player.draws}</span>
                    </td>
                    <td className="winrate-cell">{player.win_rate || 0}%</td>
                    <td className="variant-cell">{player.favorite_variant}</td>
                    <td className="lastgame-cell">
                      {formatDate(player.last_game_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ELO Rating System Modal */}
      {showEloModal && (
        <div className="elo-modal-overlay" onClick={() => setShowEloModal(false)}>
          <div className="elo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="elo-modal-header">
              <h2>📊 {RATING_INFO.title}</h2>
              <button className="elo-modal-close" onClick={() => setShowEloModal(false)}>×</button>
            </div>
            <div className="elo-modal-content">
              <p className="elo-modal-description">{RATING_INFO.description}</p>

              <div className="elo-modal-section">
                <h3>Rating Parameters</h3>
                <div className="elo-modal-details">
                  {RATING_INFO.details.map((detail, idx) => (
                    <div key={idx} className="elo-detail-row">
                      <span className="elo-detail-label">{detail.label}</span>
                      <span className="elo-detail-value">{detail.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="elo-modal-section formulas">
                <h3>📐 ELO Formulas</h3>
                <div className="formula-box">
                  <div className="formula-item">
                    <span className="formula-label">Expected Score:</span>
                    <code className="formula-code">{RATING_INFO.formulas.expected}</code>
                  </div>
                  <div className="formula-item">
                    <span className="formula-label">New Rating:</span>
                    <code className="formula-code">{RATING_INFO.formulas.newRating}</code>
                  </div>
                  <div className="formula-item">
                    <span className="formula-label">Score Values:</span>
                    <code className="formula-code">{RATING_INFO.formulas.scoreWin}</code>
                  </div>
                </div>
              </div>

              <div className="elo-modal-section blended-section">
                <h3>🌐 Blended Rating (All Variants)</h3>
                <p>{RATING_INFO.blendedInfo}</p>
                <div className="formula-box highlight">
                  <code className="formula-code">{RATING_INFO.blendedFormula}</code>
                </div>
                <p className="formula-example">
                  <strong>Example:</strong> If you have 1400 rating in Traditional (20 games) and 1600 in Breakout (10 games),
                  your Blended ELO = (1400×20 + 1600×10) / 30 = <strong>1467</strong>
                </p>
              </div>

              <div className="elo-modal-section">
                <h3>🎯 Variant-Specific Rating</h3>
                <p>{RATING_INFO.variantInfo}</p>
              </div>

              <div className="elo-modal-section custom-warning">
                <h3>⚠️ Custom Variants</h3>
                <p>{RATING_INFO.customVariantNote}</p>
              </div>

              <div className="elo-modal-section how-it-works">
                <h3>How Points Are Calculated</h3>
                <ul>
                  <li><strong>Win against higher-rated:</strong> Gain more points (up to +32)</li>
                  <li><strong>Win against lower-rated:</strong> Gain fewer points (minimum +1)</li>
                  <li><strong>Lose against higher-rated:</strong> Lose fewer points (minimum -1)</li>
                  <li><strong>Lose against lower-rated:</strong> Lose more points (up to -32)</li>
                  <li><strong>Draw:</strong> Higher-rated loses points, lower-rated gains points</li>
                </ul>
              </div>
            </div>
            <div className="elo-modal-footer">
              <button className="elo-modal-btn" onClick={() => setShowEloModal(false)}>
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
